import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, StickyNote, Clock, Loader2 } from 'lucide-react';
import { useGetCalendarEventsQuery } from '@/APIConnect';
import { useSelector } from 'react-redux';
import {
  useGetCourseByIdQuery,
  useLazyGetContentRelatedTopicDataQuery,
  useLazyGetSubtopicContentQuery,
  useLazyGetQuestionByIdQuery
} from '@/APIConnect';

// Helper component to resolve course/topic names by ID for calendar events
const CalendarEventNameResolver = ({ courseId, topicId, fallbackCourseName, fallbackTopicName, children }: {
  courseId?: string;
  topicId?: string;
  fallbackCourseName?: string;
  fallbackTopicName?: string;
  children: (props: { courseName: string; topicName: string; isLoading: boolean }) => React.ReactNode;
}) => {
  const { data: courseData, isLoading: courseLoading } = useGetCourseByIdQuery(courseId, { skip: !courseId });
  const [triggerTopic, { data: topicData, isLoading: topicLoading }] = useLazyGetContentRelatedTopicDataQuery();
  const [triggerSubtopic, { data: subtopicData, isLoading: subtopicLoading }] = useLazyGetSubtopicContentQuery();
  const [triggerTopicById, { data: topicByIdData, isLoading: topicByIdLoading }] = useLazyGetQuestionByIdQuery();
  const [topicName, setTopicName] = React.useState<string>(fallbackTopicName || (topicId ? `Unknown Topic (${topicId})` : ''));

  // Check if any name resolution is still loading
  const isLoadingNames = courseLoading || topicLoading || subtopicLoading || topicByIdLoading;

  React.useEffect(() => {
    if (topicId) {
      triggerTopic(topicId);
      triggerSubtopic(topicId);
      triggerTopicById({ id: topicId });
    }
  }, [topicId, triggerTopic, triggerSubtopic, triggerTopicById]);

  React.useEffect(() => {
    if (topicData && topicData.resultObject) {
      if (Array.isArray(topicData.resultObject) && topicData.resultObject.length > 0) {
        if (topicData.resultObject[0].subTopicName) {
          setTopicName(topicData.resultObject[0].subTopicName);
        } else if (topicData.resultObject[0].name) {
          setTopicName(topicData.resultObject[0].name);
        }
      } else if (topicData.resultObject.name) {
        setTopicName(topicData.resultObject.name);
      } else if (topicData.resultObject.Name) {
        setTopicName(topicData.resultObject.Name);
      } else if (subtopicData && subtopicData.SubTopicName) {
        setTopicName(subtopicData.SubTopicName);
      } else if (topicByIdData && topicByIdData.Name) {
        setTopicName(topicByIdData.Name);
      } else if (fallbackTopicName) {
        setTopicName(fallbackTopicName);
      } else if (topicId) {
        setTopicName(`Unknown Topic (${topicId})`);
      }
    } else if (subtopicData && subtopicData.SubTopicName) {
      setTopicName(subtopicData.SubTopicName);
    } else if (topicByIdData && topicByIdData.Name) {
      setTopicName(topicByIdData.Name);
    } else if (fallbackTopicName) {
      setTopicName(fallbackTopicName);
    } else if (topicId) {
      setTopicName(`Unknown Topic (${topicId})`);
    }
  }, [topicData, subtopicData, topicByIdData, fallbackTopicName, topicId]);

  const courseName = courseData?.resultObject?.name || fallbackCourseName || (courseId ? `Unknown Course (${courseId})` : '');
  return <>{children({ courseName, topicName, isLoading: isLoadingNames })}</>;
};

interface CalendarEvent {
  id: string;
  type: 'Live Lecture' | 'Submission' | 'Assignment' | 'Project Deadline' | 'Assignment Deadline' | 'All Day Note' | 'Timed Note';
  time: string;
  title: string;
  startTime: string;
  endTime: string;
}

interface CalendarViewProps {
  events?: CalendarEvent[];
}

const CalendarView: React.FC<CalendarViewProps> = ({ events = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewType, setViewType] = useState<'Day' | 'Week' | 'Month'>('Day');

  // Calculate week range for API
  const getWeekRange = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  };
  const { start, end } = getWeekRange(currentDate);
  const { data: apiData, isLoading } = useGetCalendarEventsQuery({ start, end });

  // Map API events, deadlines, and notes to UI events
  const apiEvents = (apiData?.events || []).map((ev: any) => ({
    id: ev.id,
    type: 'Live Lecture', // or map type if needed
    time: `${new Date(ev.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(ev.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    title: ev.description || 'Event',
    startTime: new Date(ev.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    endTime: new Date(ev.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    date: new Date(ev.startTime).toISOString().slice(0, 10)
  }));

  const apiDeadlines = (apiData?.deadlines || []).map((dl: any) => ({
    id: dl.referenceId,
    type: dl.type === 'Project' ? 'Project Deadline' : 'Assignment Deadline',
    time: new Date(dl.dueDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    title: dl.assignmentTitle,
    startTime: new Date(dl.dueDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    endTime: new Date(dl.dueDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    date: new Date(dl.dueDate).toISOString().slice(0, 10)
  }));

  const apiNotes = (apiData?.notes || []).map((note: any) => {
    // Handle note reminders - only show notes that have reminders
    console.log('Processing note for calendar:', note);

    if (note.reminderDateTime && note.noteType !== 0) {
      const reminderDate = new Date(note.reminderDateTime);
      const noteEvent = {
        id: note.id,
        type: note.noteType === 1 ? 'All Day Note' : 'Timed Note',
        time: note.noteType === 1 ? 'All Day' : reminderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        title: note.title,
        startTime: note.noteType === 1 ? '00:00' : reminderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        endTime: note.noteType === 1 ? '23:59' : reminderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        date: reminderDate.toISOString().slice(0, 10),
        courseId: note.courseId || note.CourseId || '',
        topicId: note.topicId || note.TopicId || ''
      };

      console.log('Created note event for calendar:', noteEvent);
      return noteEvent;
    }
    return null;
  }).filter(Boolean);

  // Get global course/topic lists from Redux (appDetails)
  const allCourses = useSelector((state: any) => state.appDetails?.allCourses || []);
  const allTopics = useSelector((state: any) => state.appDetails?.allTopics || []);

  // Build global mappings from Redux state (allCourses/allTopics)
  const courseIdToName = React.useMemo(() => {
    return allCourses.length > 0 ? Object.fromEntries(allCourses.map((c: any) => [c.id, c.name])) : {};
  }, [allCourses]);
  const topicIdToName = React.useMemo(() => {
    return allTopics.length > 0 ? Object.fromEntries(allTopics.map((t: any) => [t.id, t.name])) : {};
  }, [allTopics]);

  const realEvents = [...apiEvents, ...apiDeadlines, ...apiNotes];

  // Filter events for the selected date
  const selectedDateStr = selectedDate.toISOString().slice(0, 10);
  const filteredEvents = realEvents.filter(e => e.date === selectedDateStr);
  // Add courseName and topicName to each event if courseId/topicId present
  const displayEvents = filteredEvents.length > 0
    ? filteredEvents.map(e => ({
        ...e,
        courseName: e.courseId ? courseIdToName[e.courseId] || '' : '',
        topicName: e.topicId ? topicIdToName[e.topicId] || '' : ''
      }))
    : [];

  // Debug: log displayEvents to inspect courseName and topicName
  React.useEffect(() => {
    console.log('displayEvents for calendar:', displayEvents);
  }, [displayEvents]);

  // Get current week days
  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    startOfWeek.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  const weekDays = getWeekDays();
  const dayNames = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatSelectedDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric' 
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  // Change navigation to move by week instead of month
  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'Live Lecture':
        return 'bg-[#347468]';
      case 'Submission':
        return 'bg-[#347468]';
      case 'Assignment':
        return 'bg-[#347468]';
      case 'Project Deadline':
        return 'bg-[#347468]';
      case 'Assignment Deadline':
        return 'bg-[#347468]';
      case 'All Day Note':
        return 'bg-blue-500';
      case 'Timed Note':
        return 'bg-purple-500';
      default:
        return 'bg-[#347468]';
    }
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'All Day Note':
        return <StickyNote className="w-3 h-3" />;
      case 'Timed Note':
        return <Clock className="w-3 h-3" />;
      default:
        return null;
    }
  };

  // Dynamically generate timeline timestamps based on event range and interval
  function parseTimeString(time: string) {
    // Handles 'HH:MM AM/PM' format
    const [h, m] = time.split(/:| /);
    let hour = parseInt(h, 10);
    const minute = parseInt(m, 10);
    const ampm = time.slice(-2);
    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return hour * 60 + minute;
  }
  function formatTime(minutes: number) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  }
  // Generate dynamic timeline - only show times where events exist
  const generateDynamicTimeline = () => {
    if (displayEvents.length === 0) {
      return [];
    }

    // Get unique event times and sort them
    const eventTimesMap = new Map<string, boolean>();
    displayEvents.forEach(event => {
      const minutes = parseTimeString(event.startTime);
      const timeStr = formatTime(minutes);
      eventTimesMap.set(timeStr, true);
    });
    const eventTimes = Array.from(eventTimesMap.keys());

    // Sort times chronologically
    return eventTimes.sort((a, b) => parseTimeString(a) - parseTimeString(b));
  };

  const timelineTicks = generateDynamicTimeline();
  const timelineHeight = 180;
  const gap = timelineTicks.length > 1 ? timelineHeight / (timelineTicks.length - 1) : 0;

  // Show loading state while fetching calendar data
  if (isLoading) {
    return (
      <div className="w-full max-w-md mx-auto bg-white sm:rounded-2xl sm:shadow-lg flex flex-col sm:block">
        {/* Calendar Header - Same as actual */}
        <div className="flex items-center justify-center mb-4 w-full h-8 relative">
          <div className="text-center font-semibold text-[#222222] font-inter text-base leading-5">
            Loading...
          </div>
        </div>

        {/* Week Days Placeholder - Same height as actual */}
        <div className="flex justify-center mb-6 h-16 gap-2">
          {[...Array(7)].map((_, index) => (
            <div
              key={index}
              className="flex flex-col items-center justify-center w-[44.86px] h-16 gap-0.5 py-1 px-0.5 bg-gray-100 border border-[#E0E0E0] animate-pulse"
              style={{ borderRadius: '8px' }}
            >
              <div className="w-[32.86px] h-6 bg-gray-200 rounded"></div>
              <div className="w-[32.86px] h-4 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>

        {/* Calendar Frame - Same dimensions as actual */}
        <div className="bg-white border border-[#347468] w-full max-w-lg min-h-[349px] rounded-tl-2xl rounded-br-2xl rounded-bl-2xl shadow-[0px_4px_16.5px_0px_#00000040] overflow-hidden mx-auto sm:mx-0 px-1 sm:px-0">
          {/* Date Header Placeholder */}
          <div className="flex flex-row items-center justify-between w-full mt-4 gap-4 px-4">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-[37px] w-16 bg-gray-200 rounded-full animate-pulse"></div>
          </div>

          {/* Loading Content */}
          <div className="flex items-center justify-center h-[280px]">
            <div className="text-center space-y-4">
              <div className="relative">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#347468]" />
              </div>
              <p className="text-[#347468] font-inter text-sm">Loading calendar...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white sm:rounded-2xl sm:shadow-lg flex flex-col sm:block">
      {/* Calendar Header */}
      <div className="flex items-center justify-center mb-4 w-full h-8 relative">
        {/* Previous Button */}
        <button
          onClick={() => navigateWeek('prev')}
          className="absolute left-0 flex items-center justify-center border-[0.8px] border-[#E0E0E0] rounded-full w-8 h-8"
        >
          <ChevronLeft
            className="text-gray-600 w-4 h-4"
          />
        </button>

        {/* Month/Year Display */}
        <div
          className="text-center font-semibold text-[#222222] font-inter text-base leading-5"
        >
          {formatDate(currentDate)}
        </div>

        {/* Next Button */}
        <button
          onClick={() => navigateWeek('next')}
          className="absolute right-0 flex items-center justify-center border-[0.8px] border-[#E0E0E0] rounded-full w-8 h-8"
        >
          <ChevronRight
            className="text-gray-600 w-4 h-4"
          />
        </button>
      </div>

      {/* Week Days */}
      <div className="flex justify-center mb-6 h-16 gap-2">
        {weekDays.map((date, index) => {
          const isCurrentDay = isToday(date);
          const isSelectedDay = isSelected(date);
          // Check if there is an event, deadline, or note for this date
          const dateStr = date.toISOString().slice(0, 10);
          const hasEvent = realEvents.some(event => event.date === dateStr);

          return (
            <button
              key={index}
              onClick={() => setSelectedDate(date)}
              className={`flex flex-col items-center justify-center w-[44.86px] h-16 gap-0.5 py-1 px-0.5 ${
                isSelectedDay ? 'bg-[#347468]' : 'bg-white'
              } border ${isSelectedDay ? '' : 'border-[#E0E0E0]'}`}
              style={{ borderRadius: '8px' }}
            >
              {/* Date Number */}
              <div
                className={`text-center w-[32.86px] h-6 font-inter font-normal text-xl leading-6 ${
                  isSelectedDay ? 'text-white' : 'text-[#222222]'
                }`}
              >
                {date.getDate()}
              </div>

              {/* Day Name */}
              <div
                className={`text-center w-[32.86px] h-4 font-inter font-normal text-xs leading-4 ${
                  isSelectedDay ? 'text-white' : 'text-[#717171]'
                }`}
              >
                {dayNames[index]}
              </div>

              {/* Notification Dot */}
              {hasEvent && (
                <div
                  className={`rounded-full w-1 h-1 ${
                    isSelectedDay ? 'bg-white' : 'bg-[#347468]'
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Calendar Frame */}
      <div className="bg-white border border-[#347468] w-full max-w-lg min-h-[349px] rounded-tl-2xl rounded-br-2xl rounded-bl-2xl shadow-[0px_4px_16.5px_0px_#00000040] overflow-hidden mx-auto sm:mx-0 px-1 sm:px-0">
        {/* Date Header with Dropdown - Responsive, gap, and alignment */}
        <div className="flex flex-row items-center justify-between w-full mt-4 gap-4 px-4">
          {/* Selected Date - left on desktop, top on mobile */}
          <div className="text-[#2D2D2D] capitalize font-inter font-semibold text-base leading-none text-left">
            {formatSelectedDate(selectedDate)}
          </div>
          {/* Day Dropdown - right on desktop, below on mobile */}
          <button className="flex items-center bg-white border border-[#347468] rounded-full h-[37px] gap-1 px-3 py-2">
            <span className="text-[#347468] capitalize font-inter font-semibold text-base leading-none">
              {viewType}
            </span>
            <ChevronDown className="text-[#347468] w-4 h-4 stroke-[2.5]" />
          </button>
        </div>

        {/* Horizontal Line */}
        <div className="border-t border-[#347468] w-full h-0 mt-[19px]" />

        {/* Timeline and Events - Scrollable */}
        <div className="relative mt-[37px] h-[180px] overflow-y-auto px-1 sm:px-4 scrollbar-hide calendar-scroll-x" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', minWidth: 0 }}>
          {/* Show message if no events */}
          {timelineTicks.length === 0 ? (
            <div className="flex items-center justify-center h-full text-[#347468] font-inter">
              No events present
            </div>
          ) : (
            /* Dynamic Timeline - only show times with events */
            <div className="space-y-0">
              {timelineTicks.map((time, idx) => {
                // Find events for this exact time
                const eventsAtThisTime = displayEvents.filter(event => {
                  const eventMinutes = parseTimeString(event.startTime);
                  const timeMinutes = parseTimeString(time);
                  return eventMinutes === timeMinutes;
                });

                // Calculate dynamic gap to next time slot based on events in between
                let lineHeight = 0;
                if (idx < timelineTicks.length - 1) {
                  const currentMinutes = parseTimeString(time);
                  const nextMinutes = parseTimeString(timelineTicks[idx + 1]);
                  // Find events between current and next time
                  const eventsBetween = displayEvents.filter(event => {
                    const eventMinutes = parseTimeString(event.startTime);
                    return eventMinutes > currentMinutes && eventMinutes < nextMinutes;
                  });
                  // Base height: minimum 72px (increased), plus 72px per event in between
                  lineHeight = 72 + eventsBetween.length * 72;
                }

                return (
                  <div key={time} className="flex items-start gap-2 sm:gap-4 w-full min-w-0">
                    {/* Time Label and Dotted Line in one column */}
                    <div className="flex flex-col items-center w-[60px] sm:w-[80px] flex-shrink-0 text-left">
                      <div className="text-[#2D2D2D] font-inter font-semibold text-sm leading-none whitespace-nowrap mb-0.5">
                        {time}
                      </div>
                      {/* Vertical dotted line - only if not last slot */}
                      {idx < timelineTicks.length - 1 && (
                        <div
                          className="w-[2px] border-l-2 border-dotted border-[#347468] opacity-60"
                          style={{ height: `${lineHeight}px` }}
                        ></div>
                      )}
                    </div>

                    {/* Events for this time */}
                    <div className="flex-1 flex flex-col gap-2 sm:gap-3 -ml-1 sm:-ml-2 min-w-0">
                      {eventsAtThisTime.map((event) => {
                        // Only resolve names for note events (All Day Note, Timed Note)
                        if ((event.type === 'All Day Note' || event.type === 'Timed Note') && (event.courseId || event.topicId)) {
                          return (
                            <CalendarEventNameResolver
                              key={event.id}
                              courseId={event.courseId}
                              topicId={event.topicId}
                              fallbackCourseName={event.courseName}
                              fallbackTopicName={event.topicName}
                            >
                              {({ courseName, topicName, isLoading }) => (
                                <div
                                  className="bg-white border border-[#347468] rounded-xl w-full max-w-full sm:max-w-[320px] min-h-[60px] p-2 sm:p-3 flex flex-col justify-center shadow-sm"
                                >
                                  <div className="flex flex-col items-start gap-1 w-full">
                                    {/* Course and Topic Name */}
                                    {isLoading ? (
                                      <div className="mb-0.5 w-full flex flex-col items-start gap-1">
                                        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse"></div>
                                        <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                                      </div>
                                    ) : (courseName || topicName) && (
                                      <div className="mb-0.5 w-full flex flex-col items-start">
                                        {courseName && (
                                          <span className="text-[11px] text-[#347468] font-semibold truncate max-w-full">{courseName}</span>
                                        )}
                                        {topicName && (
                                          <span className="text-[11px] text-[#347468] font-semibold truncate max-w-full">{topicName}</span>
                                        )}
                                      </div>
                                    )}
                                    <div className="flex flex-row items-center gap-2 w-full">
                                      {getEventTypeIcon(event.type) && (
                                        <span className="text-[#717171] flex-shrink-0">
                                          {getEventTypeIcon(event.type)}
                                        </span>
                                      )}
                                      <span className="text-[#717171] font-inter font-normal text-[11px] leading-4 flex-shrink-0">
                                        {event.type}
                                      </span>
                                      <span className="text-[#2D2D2D] font-['DM_Sans'] font-medium text-[11px] leading-4 ml-auto">
                                        {event.time}
                                      </span>
                                    </div>
                                    <span className="text-[#347468] font-inter font-medium text-sm leading-5 truncate w-full">
                                      {event.title}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </CalendarEventNameResolver>
                          );
                        } else {
                          // For non-note events, render as before
                          return (
                        <div
                          key={event.id}
                              className="bg-white border border-[#347468] rounded-xl w-full max-w-full sm:max-w-[320px] min-h-[60px] p-2 sm:p-3 flex flex-col justify-center shadow-sm"
                        >
                          <div className="flex flex-col items-start gap-1 w-full">
                            <div className="flex flex-row items-center gap-2 w-full">
                              {getEventTypeIcon(event.type) && (
                                <span className="text-[#717171] flex-shrink-0">
                                  {getEventTypeIcon(event.type)}
                                </span>
                              )}
                              <span className="text-[#717171] font-inter font-normal text-[11px] leading-4 flex-shrink-0">
                                {event.type}
                              </span>
                              <span className="text-[#2D2D2D] font-['DM_Sans'] font-medium text-[11px] leading-4 ml-auto">
                                {event.time}
                              </span>
                            </div>
                            <span className="text-[#347468] font-inter font-medium text-sm leading-5 truncate w-full">
                              {event.title}
                            </span>
                          </div>
                        </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}


        </div>
      </div>
    </div>
  );
};

// Responsive calendar horizontal scroll for mobile
// Place this outside the component or at the bottom of the file
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @media (max-width: 639px) {
      .calendar-scroll-x {
        overflow-x: auto !important;
        min-width: 0 !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export default CalendarView;
